import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { 
  Briefcase, MapPin, Clock, ChevronDown, ChevronUp,
  Upload, Send, CheckCircle, Heart, Code, Users,
  Lightbulb, Zap, X, FileText
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ValueMarquee } from '@/components/ui/MarqueeText';
import { 
  GSAPTextReveal, 
  ScrollScaleReveal,
  StaggeredCardsContainer,
  MaskReveal,
  MagneticButton,
  ParallaxElement,
  NeonGlowLine
} from '@/components/ui/GSAPAnimations';

const cultureValues = [
  'Ownership',
  'Innovation',
  'Empathy',
  'Code Quality',
  'Continuous Learning',
  'Transparency',
  'Customer First',
  'Work-Life Balance',
  'Ownership',
  'Innovation',
  'Empathy',
  'Code Quality',
];

const departments = ['All', 'Engineering', 'Product', 'Design', 'Sales', 'Operations', 'Marketing'];

const openRoles = [
  {
    id: 1,
    title: 'Senior Full Stack Engineer',
    subtitle: 'React / Node.js / Python',
    location: 'Bengaluru',
    type: 'Full Time',
    experience: '4-7 years',
    department: 'Engineering',
    tattva: 'Ajiva',
    description: 'Own and build critical product features end-to-end. You\'ll architect scalable microservices, design performant React interfaces, and ship code that serves 10 lakh+ students daily.',
    requirements: [
      '4+ years building production web applications',
      'Strong proficiency in React, Node.js, and Python/FastAPI',
      'Experience with MongoDB, PostgreSQL, and Redis',
      'Track record of shipping features in agile teams',
      'Understanding of CI/CD, Docker, and cloud (AWS)',
    ],
    niceToHave: [
      'Experience building multi-tenant SaaS platforms',
      'Familiarity with real-time systems (WebSockets, SSE)',
      'Prior EdTech or ERP domain experience',
    ],
  },
  {
    id: 2,
    title: 'Frontend Engineer',
    subtitle: 'React / Tailwind / GSAP',
    location: 'Remote',
    type: 'Full Time',
    experience: '2-4 years',
    department: 'Engineering',
    tattva: 'Nirjara',
    description: 'Craft pixel-perfect, performant interfaces that 500+ institutions interact with daily. Focus on animation, accessibility, and responsive design across our product suite.',
    requirements: [
      '2+ years with React and modern CSS (Tailwind preferred)',
      'Experience with animation libraries (Framer Motion, GSAP)',
      'Strong eye for design and attention to UI detail',
      'Knowledge of web performance optimization',
      'Familiarity with component libraries and design systems',
    ],
    niceToHave: [
      'Experience with Next.js or similar SSR frameworks',
      'Portfolio with interactive/animated web projects',
      'Accessibility (WCAG) knowledge',
    ],
  },
  {
    id: 3,
    title: 'Backend Engineer',
    subtitle: 'Python / FastAPI / MongoDB',
    location: 'Bengaluru',
    type: 'Full Time',
    experience: '3-5 years',
    department: 'Engineering',
    tattva: 'Ajiva',
    description: 'Build the backbone of our ERP platform. Design APIs that handle millions of transactions, optimize database queries, and ensure 99.9% uptime for mission-critical education systems.',
    requirements: [
      '3+ years with Python (FastAPI/Django preferred)',
      'Deep experience with MongoDB and database optimization',
      'Experience building RESTful APIs at scale',
      'Knowledge of message queues (RabbitMQ/Redis)',
      'Understanding of microservices architecture',
    ],
    niceToHave: [
      'Experience with Kubernetes and container orchestration',
      'Background in high-availability systems',
      'Familiarity with event-driven architecture',
    ],
  },
  {
    id: 4,
    title: 'Data Scientist',
    subtitle: 'ML / Predictive Analytics',
    location: 'Remote',
    type: 'Full Time',
    experience: '3-6 years',
    department: 'Engineering',
    tattva: 'Ashrava',
    description: 'Transform institutional data into actionable insights. Build predictive models for student performance, dropout risk, and resource optimization that directly improve educational outcomes.',
    requirements: [
      '3+ years in data science or ML engineering',
      'Strong Python skills (pandas, scikit-learn, PyTorch)',
      'Experience with statistical analysis and A/B testing',
      'Knowledge of SQL and data pipeline tools',
      'Ability to communicate insights to non-technical stakeholders',
    ],
    niceToHave: [
      'Experience with educational data mining',
      'Published research or Kaggle experience',
      'Experience with recommendation systems or NLP',
    ],
  },
  {
    id: 5,
    title: 'Product Manager',
    subtitle: 'ERP Suite / Growth',
    location: 'Bengaluru',
    type: 'On-site',
    experience: '4-8 years',
    department: 'Product',
    tattva: 'Moksha',
    description: 'Own the product roadmap for our School ERP or College ERP. Drive product-led growth, define metrics, and collaborate with engineering to ship features that institutions love.',
    requirements: [
      '4+ years in product management (B2B SaaS preferred)',
      'Experience managing enterprise product roadmaps',
      'Strong analytical skills and data-driven decision making',
      'Excellent stakeholder management and communication',
      'Experience with user research and discovery',
    ],
    niceToHave: [
      'EdTech or education industry background',
      'Technical background (CS/engineering degree)',
      'Experience with NEP 2020 compliance requirements',
    ],
  },
  {
    id: 6,
    title: 'UI/UX Designer',
    subtitle: 'Product Design / Design Systems',
    location: 'Bengaluru',
    type: 'Hybrid',
    experience: '3-5 years',
    department: 'Design',
    tattva: 'Nirjara',
    description: 'Design intuitive experiences for complex institutional workflows. Own the design system, conduct user research with school administrators and teachers, and ship designs that simplify the complex.',
    requirements: [
      '3+ years in product/UX design for B2B software',
      'Strong Figma skills and design system experience',
      'Portfolio demonstrating complex workflow simplification',
      'Experience with user research and usability testing',
      'Understanding of accessibility and inclusive design',
    ],
    niceToHave: [
      'Experience designing dashboards and data-heavy UIs',
      'Motion design skills (After Effects, Lottie)',
      'Familiarity with front-end implementation',
    ],
  },
  {
    id: 7,
    title: 'Enterprise Sales Executive',
    subtitle: 'Institutional Sales / K-12 & Higher Ed',
    location: 'Bengaluru / Mumbai',
    type: 'On-site',
    experience: '3-6 years',
    department: 'Sales',
    tattva: 'Bandha',
    description: 'Drive new business by building relationships with school principals, college directors, and education administrators across India. Own the full sales cycle from prospecting to close.',
    requirements: [
      '3+ years in B2B enterprise sales (SaaS preferred)',
      'Experience selling to educational institutions or government',
      'Proven track record of meeting/exceeding quotas',
      'Excellent presentation and negotiation skills',
      'Willingness to travel (30-40% of time)',
    ],
    niceToHave: [
      'Existing relationships in the education sector',
      'Experience with long-cycle enterprise deals (3-6 months)',
      'Regional language proficiency (Hindi, Kannada, Tamil)',
    ],
  },
  {
    id: 8,
    title: 'DevOps / SRE Engineer',
    subtitle: 'AWS / Kubernetes / CI-CD',
    location: 'Remote',
    type: 'Full Time',
    experience: '3-5 years',
    department: 'Engineering',
    tattva: 'Samvara',
    description: 'Ensure 99.9% uptime for 500+ institutions relying on our platform daily. Build and maintain infrastructure, automate deployments, and implement security best practices.',
    requirements: [
      '3+ years in DevOps/SRE roles',
      'Strong experience with AWS (EC2, ECS, RDS, S3)',
      'Hands-on with Docker, Kubernetes, and Terraform',
      'Experience with CI/CD pipelines (GitHub Actions, Jenkins)',
      'Knowledge of monitoring (Prometheus, Grafana, ELK)',
    ],
    niceToHave: [
      'ISO 27001 or SOC 2 compliance experience',
      'Experience with multi-tenant infrastructure',
      'Database administration (MongoDB, PostgreSQL)',
    ],
  },
  {
    id: 9,
    title: 'Customer Success Manager',
    subtitle: 'Onboarding / Account Management',
    location: 'Bengaluru',
    type: 'Hybrid',
    experience: '2-4 years',
    department: 'Operations',
    tattva: 'Bandha',
    description: 'Be the bridge between Tattva Techworkz and our institutional clients. Drive onboarding, ensure adoption, reduce churn, and turn customers into advocates.',
    requirements: [
      '2+ years in customer success or account management',
      'Experience with SaaS onboarding and training',
      'Strong communication and relationship-building skills',
      'Comfort with data analysis and reporting',
      'Ability to manage 30-50 accounts simultaneously',
    ],
    niceToHave: [
      'Experience with educational institutions',
      'Technical aptitude to demo software features',
      'Project management certification (PMP, CSM)',
    ],
  },
  {
    id: 10,
    title: 'Digital Marketing Lead',
    subtitle: 'SEO / Content / Performance Marketing',
    location: 'Bengaluru',
    type: 'Hybrid',
    experience: '4-6 years',
    department: 'Marketing',
    tattva: 'Jiva',
    description: 'Drive inbound demand for Tattva Techworkz products. Own SEO, content strategy, paid campaigns, and lead generation funnels targeting educational institutions across India.',
    requirements: [
      '4+ years in B2B digital marketing',
      'Proven track record in SEO and content marketing',
      'Experience with Google Ads, LinkedIn Ads, and Meta',
      'Strong analytical skills (GA4, HubSpot, or similar)',
      'Ability to create compelling content for education buyers',
    ],
    niceToHave: [
      'Experience marketing to institutional decision-makers',
      'Video content creation and YouTube strategy',
      'Marketing automation (HubSpot, Marketo)',
    ],
  },
];

const perks = [
  { icon: Heart, title: 'Health Insurance', desc: 'Comprehensive coverage for you and family' },
  { icon: Code, title: 'Learning Budget', desc: '₹50K/year for courses and conferences' },
  { icon: Users, title: 'Remote First', desc: 'Work from anywhere in India' },
  { icon: Lightbulb, title: 'Innovation Days', desc: 'Monthly hackathons and innovation time' },
  { icon: Zap, title: 'Fast Growth', desc: 'Clear promotion paths and mentorship' },
  { icon: Briefcase, title: 'Equity Options', desc: 'Own a piece of the company you\'re building' },
];

const CareersPage = () => {
  const [expandedRole, setExpandedRole] = useState(null);
  const [activeDepartment, setActiveDepartment] = useState('All');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    message: '',
    resume: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, resume: file }));
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFormData(prev => ({ ...prev, resume: e.dataTransfer.files[0] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setSubmitSuccess(true);
    
    setTimeout(() => {
      setSubmitSuccess(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        role: '',
        message: '',
        resume: null,
      });
    }, 3000);
  };

  return (
    <div className="pt-20 min-h-screen bg-void">
      {/* ============================================
          HERO SECTION
          ============================================ */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-mesh-gradient" />
        
        {/* Decorative Elements */}
        <ParallaxElement speed={-0.2} className="absolute top-20 left-10 opacity-20">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
            className="w-48 h-48 border border-orange/30 rounded-full"
          />
        </ParallaxElement>

        <ParallaxElement speed={0.15} className="absolute bottom-20 right-20 opacity-20">
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
            className="w-64 h-64 border border-teal/30 rounded-full"
          />
        </ParallaxElement>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MaskReveal direction="up">
            <span className="font-mono text-xs text-orange tracking-widest uppercase mb-4 block">
              Careers at Tattva Techworkz
            </span>
          </MaskReveal>

          <GSAPTextReveal
            text="More than a job."
            className="text-display text-4xl sm:text-5xl lg:text-6xl text-foreground mb-2"
            splitBy="words"
            staggerDelay={0.1}
          />
          <GSAPTextReveal
            text="A family that grows together."
            className="text-display text-4xl sm:text-5xl lg:text-6xl text-gradient-orange mb-6"
            splitBy="words"
            staggerDelay={0.1}
            delay={0.5}
          />

          <MaskReveal direction="up">
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
              Join 80+ builders transforming education for 10 lakh+ students across 500+ institutions.
              We ship fast, think big, and care deeply about impact.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-2 px-4 py-2 rounded-full border border-glass-border">
                <Briefcase className="w-4 h-4 text-teal" />
                {openRoles.length} Open Positions
              </span>
              <span className="flex items-center gap-2 px-4 py-2 rounded-full border border-glass-border">
                <MapPin className="w-4 h-4 text-orange" />
                Bengaluru &bull; Remote &bull; Hybrid
              </span>
            </div>
          </MaskReveal>
        </div>
      </section>

      {/* ============================================
          VALUES MARQUEE - Like Student Tribe
          ============================================ */}
      <section className="py-6 border-y border-glass-border bg-void/50 overflow-hidden">
        <ValueMarquee values={cultureValues} />
      </section>

      {/* ============================================
          CULTURE & PERKS
          ============================================ */}
      <section className="py-24 bg-void-light/30 relative">
        <div className="absolute top-0 left-0 right-0">
          <NeonGlowLine />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollScaleReveal className="text-center mb-16">
            <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
              Why Join Us
            </span>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground">
              Life at <span className="text-gradient-teal">Tattva</span>
            </h2>
          </ScrollScaleReveal>

          <StaggeredCardsContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {perks.map((perk, index) => (
              <motion.div
                key={perk.title}
                className="glass-card-hover p-6 flex items-start gap-4"
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center flex-shrink-0 group-hover:bg-teal/20 transition-colors">
                  <perk.icon className="w-6 h-6 text-teal" />
                </div>
                <div>
                  <h3 className="text-foreground font-display font-semibold mb-1">
                    {perk.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">{perk.desc}</p>
                </div>
              </motion.div>
            ))}
          </StaggeredCardsContainer>
        </div>
      </section>

      {/* ============================================
          OPEN ROLES
          ============================================ */}
      <section className="py-24 bg-void">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollScaleReveal className="text-center mb-12">
            <span className="font-mono text-xs text-orange tracking-widest uppercase mb-4 block">
              Open Positions
            </span>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground">
              Find Your <span className="text-gradient-orange">Role</span>
            </h2>
          </ScrollScaleReveal>

          {/* Department Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {departments.map((dept) => (
              <button
                key={dept}
                onClick={() => setActiveDepartment(dept)}
                className={cn(
                  'px-4 py-2 rounded-full text-sm font-medium transition-all duration-300',
                  activeDepartment === dept
                    ? 'bg-teal text-primary-foreground shadow-teal-glow'
                    : 'border border-glass-border text-muted-foreground hover:text-foreground hover:border-teal/50'
                )}
              >
                {dept}
                {dept !== 'All' && (
                  <span className="ml-1 text-xs opacity-70">
                    ({openRoles.filter(r => r.department === dept).length})
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Accordion List */}
          <div className="space-y-4">
            {openRoles.filter(role => activeDepartment === 'All' || role.department === activeDepartment).map((role, index) => (
              <motion.div
                key={role.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card overflow-hidden"
              >
                {/* Role Header */}
                <motion.button
                  onClick={() => setExpandedRole(expandedRole === role.id ? null : role.id)}
                  className="w-full p-6 flex items-center justify-between text-left hover:bg-glass-hover transition-colors"
                  whileHover={{ x: 5 }}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-display text-xl text-foreground">
                        {role.title}
                      </h3>
                      <span className="px-2 py-0.5 text-xs font-mono rounded-full bg-orange/10 text-orange">
                        {role.tattva}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{role.subtitle}</p>
                    <div className="flex flex-wrap items-center gap-4 mt-3">
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="w-3 h-3" />
                        {role.location}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {role.type}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Briefcase className="w-3 h-3" />
                        {role.experience}
                      </span>
                    </div>
                  </div>
                  <motion.div
                    animate={{ rotate: expandedRole === role.id ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  </motion.div>
                </motion.button>

                {/* Expanded Content */}
                <AnimatePresence>
                  {expandedRole === role.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 pt-2 border-t border-glass-border">
                        <p className="text-muted-foreground mb-6">
                          {role.description}
                        </p>

                        <div className="grid md:grid-cols-2 gap-6 mb-6">
                          <div>
                            <h4 className="text-sm font-semibold text-foreground mb-3">
                              Requirements
                            </h4>
                            <ul className="space-y-2">
                              {role.requirements.map((req, i) => (
                                <motion.li 
                                  key={i} 
                                  className="flex items-start gap-2 text-sm text-muted-foreground"
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: i * 0.05 }}
                                >
                                  <CheckCircle className="w-4 h-4 text-teal mt-0.5 flex-shrink-0" />
                                  {req}
                                </motion.li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <h4 className="text-sm font-semibold text-foreground mb-3">
                              Nice to Have
                            </h4>
                            <ul className="space-y-2">
                              {role.niceToHave.map((item, i) => (
                                <motion.li 
                                  key={i} 
                                  className="flex items-start gap-2 text-sm text-muted-foreground"
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: i * 0.05 }}
                                >
                                  <span className="text-orange">+</span>
                                  {item}
                                </motion.li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        <MagneticButton strength={20}>
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                              setFormData(prev => ({ ...prev, role: role.title }));
                              document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            className="btn-primary"
                          >
                            Apply Now
                            <Send className="w-4 h-4" />
                          </motion.button>
                        </MagneticButton>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          APPLICATION FORM
          ============================================ */}
      {/* ============================================
          DON'T SEE YOUR ROLE?
          ============================================ */}
      <section className="py-16 bg-void border-t border-glass-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-display text-2xl text-foreground mb-4">
            Don&apos;t see your role?
          </h3>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            We&apos;re always looking for exceptional talent. Send us your resume and tell us
            how you&apos;d contribute to transforming education technology.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="mailto:hr@tattvatechworkz.com?subject=Open Application - [Your Role]"
              className="btn-ghost inline-flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              hr@tattvatechworkz.com
            </a>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Include your resume, portfolio (if applicable), and a brief note about what excites you about Tattva Techworkz.
          </p>
        </div>
      </section>

      {/* ============================================
          APPLICATION FORM
          ============================================ */}
      <section id="application-form" className="py-24 bg-void-light/30">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollScaleReveal className="text-center mb-12">
            <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
              Apply Now
            </span>
            <h2 className="text-display text-3xl text-foreground">
              Join the <span className="text-gradient-teal">Journey</span>
            </h2>
            <p className="text-muted-foreground text-sm mt-2">
              Or email your application directly to <a href="mailto:hr@tattvatechworkz.com" className="text-teal hover:underline">hr@tattvatechworkz.com</a>
            </p>
          </ScrollScaleReveal>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="glass-card p-4 sm:p-8"
          >
            <AnimatePresence mode="wait">
              {submitSuccess ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="flex flex-col items-center justify-center py-12"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                    className="w-20 h-20 rounded-full bg-teal/20 flex items-center justify-center mb-6"
                  >
                    <CheckCircle className="w-10 h-10 text-teal" />
                  </motion.div>
                  <h3 className="text-display text-2xl text-teal mb-2">
                    Application Submitted!
                  </h3>
                  <p className="text-muted-foreground">
                    We&apos;ll get back to you within 3-5 business days.
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  className="space-y-6"
                >
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Full Name
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your name"
                      required
                      className="bg-void-light border-glass-border text-foreground placeholder:text-muted-foreground focus:border-teal"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Email
                      </label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="you@email.com"
                        required
                        className="bg-void-light border-glass-border text-foreground placeholder:text-muted-foreground focus:border-teal"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Phone
                      </label>
                      <Input
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+91 98765 43210"
                        className="bg-void-light border-glass-border text-foreground placeholder:text-muted-foreground focus:border-teal"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Position Applying For
                    </label>
                    <Input
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      placeholder="Select from open roles above"
                      required
                      className="bg-void-light border-glass-border text-foreground placeholder:text-muted-foreground focus:border-teal"
                    />
                  </div>

                  {/* Resume Upload with Drag & Drop */}
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Resume
                    </label>
                    <div 
                      className={cn(
                        'relative border-2 border-dashed rounded-xl p-8 text-center',
                        'transition-all duration-300 cursor-pointer',
                        dragActive && 'border-teal bg-teal/5 scale-[1.02]',
                        formData.resume 
                          ? 'border-teal bg-teal/5' 
                          : 'border-glass-border hover:border-muted-foreground'
                      )}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                    >
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      {formData.resume ? (
                        <motion.div 
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="flex items-center justify-center gap-3"
                        >
                          <FileText className="w-6 h-6 text-teal" />
                          <span className="text-teal font-medium">{formData.resume.name}</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setFormData(prev => ({ ...prev, resume: null }));
                            }}
                            className="min-w-[44px] min-h-[44px] p-2.5 flex items-center justify-center rounded-full hover:bg-glass-hover"
                          >
                            <X className="w-4 h-4 text-muted-foreground" />
                          </button>
                        </motion.div>
                      ) : (
                        <div className="flex flex-col items-center gap-2 text-muted-foreground">
                          <motion.div
                            animate={{ y: [0, -5, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                          >
                            <Upload className="w-8 h-8" />
                          </motion.div>
                          <span>Drag &amp; drop or click to upload</span>
                          <span className="text-xs">PDF, DOC, DOCX (Max 5MB)</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Cover Note (Optional)
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us why you'd be a great fit..."
                      rows={4}
                      className="bg-void-light border-glass-border text-foreground placeholder:text-muted-foreground resize-none focus:border-teal"
                    />
                  </div>

                  <MagneticButton strength={20} className="w-full">
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="btn-primary w-full py-4 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full"
                        />
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Submit Application
                        </>
                      )}
                    </motion.button>
                  </MagneticButton>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.form>
        </div>
      </section>
      {/* ============================================
          HIRING PROCESS
          ============================================ */}
      <section className="py-24 bg-void">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollScaleReveal className="text-center mb-16">
            <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
              Our Process
            </span>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground">
              How We <span className="text-gradient-teal">Hire</span>
            </h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
              Transparent, respectful, and fast. We respect your time and aim to complete
              the process within 2 weeks.
            </p>
          </ScrollScaleReveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Application', desc: 'Submit your resume and cover note. We review every application within 48 hours.' },
              { step: '02', title: 'Screening Call', desc: '30-min call with HR to understand your goals, experience, and cultural fit.' },
              { step: '03', title: 'Technical Round', desc: 'Role-specific assessment. Could be a coding challenge, portfolio review, or case study.' },
              { step: '04', title: 'Final Interview', desc: 'Meet the team lead and founders. Culture fit, vision alignment, and offer discussion.' },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative"
              >
                <div className="glass-card p-6 h-full">
                  <span className="text-3xl font-display font-bold text-teal/20 mb-3 block">
                    {item.step}
                  </span>
                  <h4 className="text-foreground font-display font-semibold mb-2">{item.title}</h4>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          EQUAL OPPORTUNITY FOOTER
          ============================================ */}
      <section className="py-12 bg-void-light/30 border-t border-glass-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground">
            Tattva Techworkz is an equal opportunity employer. We celebrate diversity and are committed
            to creating an inclusive environment for all employees regardless of race, gender, disability,
            religion, or sexual orientation.
          </p>
          <p className="text-xs text-muted-foreground/60 mt-3">
            Questions? Reach out to <a href="mailto:hr@tattvatechworkz.com" className="text-teal hover:underline">hr@tattvatechworkz.com</a> &bull; +91 89519 64093
          </p>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
